import { escapeRegex } from "../../helpers";
import { SourceType } from "../constants/restRequestConstants";
import { restRequestUtils } from "./restRequestUtils";

function resolveEnvironmentVariables(request, questions, isDesignMode) {
  const envVars = request.envVars || [];

  envVars.forEach((envVar) => {
    if (!envVar.enabled || !envVar.key) {
      return;
    }

    // Get the resolved value, or if it is a credential, just get the credentialId
    const valueOrCredential = restRequestUtils.resolveFieldValue(envVar, questions, isDesignMode);

    if (valueOrCredential && typeof valueOrCredential === 'object' && valueOrCredential.isCredential) {
      if (!request.credentialVars) {
        request.credentialVars = [];
      }

      // The credential will be resolved on the server (api-integrations)
      request.credentialVars.push({
        envVarKey: envVar.key,
        credentialId: valueOrCredential.credentialId,
        credentialField: valueOrCredential.credentialField
      });

      // Skip the replacement for now - will be handled server-side
      return;
    }

    const value = valueOrCredential;

    const safeKey = escapeRegex(envVar.key);
    const regex = new RegExp(`${safeKey}`, "g");

    // URL
    request.url = _replaceEnvVar(request.url, regex, value);

    // Auth
    _processAuthFields(request.auth, regex, value);

    // Headers, query, body
    request.headers = _processKeyValueArray(request.headers, regex, value);
    request.queryParams = _processKeyValueArray(request.queryParams, regex, value);
    request.body.urlEncoded = _processKeyValueArray(request.body.urlEncoded, regex, value);

    // FormData
    request.body.formData = request.body.formData.map((formData) => ({
      ...formData,
      key: _replaceEnvVar(formData.key, regex, value),
      value:
        // Don't replace value if it's a file attachment
        formData.source === SourceType.FIXED
          ? _replaceEnvVar(formData.value, regex, value)
          : formData.value,
    }));

    // Raw body
    request.body.raw = _replaceEnvVar(request.body.raw, regex, value);

    // Mappings
    request.mappings = request.mappings.map((mapping) => {
      const updatedMapping = {
        ...mapping,
        responsePath: _replaceEnvVar(mapping.responsePath, regex, value)
      };

      if (mapping.filename?.source === 'fixed') {
        updatedMapping.filename = {
          ...mapping.filename,
          value: _replaceEnvVar(mapping.filename.value, regex, value)
        };
      }

      if (mapping.contentType?.source === 'fixed') {
        updatedMapping.contentType = {
          ...mapping.contentType,
          value: _replaceEnvVar(mapping.contentType.value, regex, value)
        };
      }

      return updatedMapping;
    });
  });
}

function _replaceEnvVar(value, regex, replacement) {
  return typeof value === "string" ? value.replace(regex, replacement) : value;
}

function _processKeyValueArray(items, regex, replacement) {
  return items.map((item) => ({
    ...item,
    key: _replaceEnvVar(item.key, regex, replacement),
    value: _replaceEnvVar(item.value, regex, replacement),
  }));
}

function _processAuthFields(auth, regex, replacement) {
  if (!auth) return;

  const authFields = [
    "apiKeyName",
    "usernameKeyName",
    "passwordKeyName",
    "clientIdKeyName",
    "clientSecretKeyName",
    "tokenUrl",
    "scope",
  ];

  authFields.forEach((field) => {
    if (auth[field]) {
      auth[field] = _replaceEnvVar(auth[field], regex, replacement);
    }
  });
}

export const envVarProcessor = {
  resolveEnvironmentVariables,
};
